import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Injector,
  linkedSignal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { EditPageComponent, PageHeaderComponent } from '@softapps/core/pages';
import {
  ApplicationModule,
  ArchiveStore,
  backNavigation,
  handleRequestError,
  menuItems,
  routeParamAsNumber,
  showRequestErrors,
  SOFTLINE_FEATURE_ARCHIVE,
  subtitle,
  title } from '@softline/application';
import { authorizationFn } from '@softline/auth';
import { ApiResourceLocation, Store } from '@softline/core';
import {
  DefinitionStore,
  DynamicFormComponent,
  DynamicModule,
  SOFTLINE_FEATURE_DEFINITIONS } from '@softline/dynamic';
import {
  MessageBarService,
  modalFn,
  openFileFn,
  translator } from '@softline/ui-core';
import { filter, map, of, switchMap } from 'rxjs';
import {
  ARCHIVE_KEY_RESOURCE_PATH,
  SOFTLINE_DEFINITION_ARBEITSBERICHT_EDIT,
  SOFTLINE_DEFINITION_ARBEITSBERICHT_LIST } from '../../arbeitsbericht.api';
import {
  ArbeitsberichtConfig,
  SOFTLINE_CONFIG_ARBEITSBERICHT } from '../../arbeitsbericht.config';
import { SOFTLINE_PERMISSION_TIME_REPORT_PARTY } from '../../arbeitsbericht.permissions';
import { Objekt } from '../../models/objekt.model';
import { TimeReport } from '../../models/time-report.model';
import { TimeReportRepository } from '../../repository/time-report.repository';
import { TimeReportService } from '../../services/time-report.service';
import { TimeReportsService } from '../../services/time-reports.service';

import { ChipComponent } from '@softline/ui-core';
import { ChipGroupComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-time-report-edit',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.css'],
  imports: [
    CommonModule,
    DynamicModule,
    ReactiveFormsModule,
    ApplicationModule,
    EditPageComponent,
    PageHeaderComponent,
    ChipComponent,
    ChipGroupComponent,
    TranslatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [TimeReportService, TimeReportRepository] })
export class EditComponent {
  private readonly injector = inject(Injector);
  private readonly messageBarService = inject(MessageBarService);
  private readonly timeReportService = inject(TimeReportService);
  private readonly timeReportsService = inject(TimeReportsService);

  openFile = openFileFn();
  modal = modalFn();
  private readonly isAuthorized = authorizationFn();

  private readonly store = inject(Store);

  private readonly router = inject(Router);

  private readonly t = translator();

  private readonly timeReportId = routeParamAsNumber('id');

  private readonly definitions = this.store.signal(
    SOFTLINE_FEATURE_DEFINITIONS,
    DefinitionStore.getters.definitions
  );

  readonly timeReportConfig: ArbeitsberichtConfig = inject(
    SOFTLINE_CONFIG_ARBEITSBERICHT
  );

  readonly form = viewChild<DynamicFormComponent<TimeReport>>('form');
  private readonly formValid = toSignal(
    toObservable(this.form).pipe(
      filter((form): form is DynamicFormComponent<any> => !!form && !!form.form()),
      switchMap((form) =>
        !form?.form
          ? of(false)
          : toObservable(form.form, { injector: this.injector }).pipe(
              switchMap((form) =>
                form.statusChanges.pipe(map((status) => status === 'VALID'))
              )
            )
      )
    ),
    { initialValue: false }
  );

  readonly timeReport = this.timeReportService.value;

  readonly headerDefinition = computed(() => {
    const definitions = this.definitions();
    return definitions[SOFTLINE_DEFINITION_ARBEITSBERICHT_LIST];
  });
  readonly definition = computed(() => {
    const definitions = this.definitions();
    return definitions[SOFTLINE_DEFINITION_ARBEITSBERICHT_EDIT];
  });

  readonly allowDelete = linkedSignal(() => {
    return this.timeReportConfig?.allowDelete ?? false;
  });

  readonly hasPartyPermission = this.isAuthorized(SOFTLINE_PERMISSION_TIME_REPORT_PARTY);

  // private subscription?: Subscription;
  // private commandSubscription?: Subscription;
  // saving$ = new BehaviorSubject<boolean>(false);
  readonly GOOGLE_LINK = 'https://www.google.com/maps/search/?api=1&query=';

  constructor() {
    title(this.t('#TIME_REPORT.TITLE'));
    subtitle(this.t('#TIME_REPORT.PAGES.EDIT.SUBTITLE'));
    backNavigation('/arbeitsbericht');
    menuItems(() => [
      {
        name: 'delete',
        class: 'soft-button',
        icon: 'fa-regular fa-trash',
        execute: () => this.delete(),
        canExecute: this.allowDelete,
        priority: 1,
        type: 'execute',
        outlet: 'responsive',
        title: 'Löschen' },
      {
        type: 'execute',
        icon: 'fa-regular fa-save',
        name: 'save',
        title: 'Speichern',
        class: 'soft-button accent',
        outlet: 'responsive',
        execute: () => this.update(),
        canExecute: this.formValid },
      {
        type: 'route',
        icon: 'fa-regular fa-plus',
        name: 'create',
        title: 'Neuer Eintrag',
        priority: 1,
        outlet: 'nav',
        routerLink: ['/arbeitsbericht', 'create'] },
      {
        name: 'arbeitsberichtOverview',
        type: 'route',
        priority: 2,
        outlet: 'nav',
        icon: 'fa-regular fa-calendar-check',
        title: '#TIME_REPORT.MENU.LIST',
        routerLink: ['/arbeitsbericht'] },
      {
        name: 'arbeitsberichtQuery',
        type: 'route',
        outlet: 'nav',
        priority: 3,
        icon: 'fa-regular fa-magnifying-glass',
        title: '#TIME_REPORT.MENU.QUERY',
        routerLink: ['/arbeitsbericht', 'query'] },
      {
        name: 'arbeitsberichtEdit',
        type: 'route',
        outlet: 'nav',
        priority: 4,
        icon: 'fa-regular fa-edit',
        title: 'Bearbeiten',
        routerLink: ['/arbeitsbericht', this.timeReportId(), 'edit'] },
      {
        name: 'imageUpload',
        type: 'execute',
        icon: 'fa-regular fa-upload',
        title: 'Bild hochladen',
        execute: () => this.uploadImage(),
        outlet: 'action',
        canExecute: true }]);
  }
  // * METHODS * //
  private readonly update = async (): Promise<void> => {
    const formValue = this.form()?.value();
    if (formValue) {
      try {
        await this.timeReportService.update(formValue, { id: this.timeReportId() ?? 0 });
        this.messageBarService.success('Eintrag wurde aktualisiert!');
        await this.router.navigate(['/arbeitsbericht']);
      } catch (e) {
        handleRequestError(e, this.injector, {
          show: true });
      }
    }
  };

  private readonly delete = async (): Promise<void> => {
    if (!this.allowDelete()) {
      this.messageBarService.error('Löschen ist nicht erlaubt!');
      return;
    }
    const id = this.timeReportId();
    if (!id) return;
    try {
      await this.timeReportService.delete({ id });
      this.timeReportsService.reload();
      this.messageBarService.success('Eintrag wurde gelöscht!');
      await this.router.navigate(['/arbeitsbericht']);
    } catch (e) {
      handleRequestError(e, this.injector, {
        show: true });
    }
  };

  private readonly getConnectionResourceLocation = (id: number): ApiResourceLocation => {
    return {
      path: ARCHIVE_KEY_RESOURCE_PATH,
      pathParams: { id } };
  };

  private readonly uploadImage = async (): Promise<void> => {
    const timeReport = this.timeReport();
    if (!timeReport) return;
    const file = await this.openFile({
      title: 'Bild hochladen' });
    if (!file || file === 'DISMISSED') return;
    //TODO: ??? CREATE ARCHIVE KEY IF NOT EXISTS and save it to timeReport.archivekey ???
    const archiveKey =
      timeReport.archivekey ?? this.getConnectionResourceLocation(Number(timeReport.id));
    try {
      await this.store.dispatch(SOFTLINE_FEATURE_ARCHIVE, ArchiveStore.actions.upload, {
        archiveKey: archiveKey,
        files: Array.isArray(file) ? file : [file] });
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  };

  readonly getGoogleLink = (object: Objekt): string | undefined => {
    let link = this.GOOGLE_LINK;
    if (object.geodaten !== null) {
      link += object?.geodaten?.gpsbreite + ',' + object?.geodaten?.gpslaenge;
      return encodeURI(link);
    }
    return undefined;
  };
}
